import {
  canBookHardSkills,
  canBookScreening,
  CandidateRole,
  CandidateRoleScreeningStatus,
  formatScheduledSlot,
  getRoleOutcome,
  hasBookedInterview,
  isCallCompleted,
  isRoleVerified,
  isTestCompleted,
  RoleStepKey,
} from '@internal/components/app/roles';
import { isInterviewCompleted } from '@internal/components/app/roles/role-types';
import { resolveProjectRole } from '@internal/components/projects/helper/project-role';
import { Project } from '@internal/types/projects';

import { RoleProgress, ScheduledCallKind } from './application-model.types';

const getScheduledCall = (
  role: CandidateRole
): { kind: ScheduledCallKind; bookedAt: string | null } | null => {
  if (hasBookedInterview(role) && !isInterviewCompleted(role)) {
    return {
      kind: ScheduledCallKind.Technical,
      bookedAt: role.assessment.interview.bookedAt,
    };
  }
  if (
    role.assessment.screening.status === CandidateRoleScreeningStatus.Booked
  ) {
    return {
      kind: ScheduledCallKind.Screening,
      bookedAt: role.assessment.screening.bookedAt,
    };
  }
  return null;
};

export const getRoleProgress = (
  project: Project,
  roles: CandidateRole[]
): RoleProgress => {
  const { role, label } = resolveProjectRole(project, roles);
  const empty: RoleProgress = {
    role,
    label,
    started: false,
    verified: false,
    seniority: null,
    stopped: false,
    capacityWaitlisted: false,
    outcomeLabel: null,
    scheduledAt: null,
    scheduledKind: null,
    needsBooking: false,
    technicalDone: false,
    waiting: false,
  };
  if (!role?.started) return empty;

  const verified = isRoleVerified(role);
  const outcome = getRoleOutcome(role);
  const stopped = Boolean(outcome);
  const scheduled = getScheduledCall(role);
  const bookedAt = scheduled?.bookedAt ?? null;
  const inProgress = !verified && !stopped;
  const needsBooking =
    inProgress && (canBookHardSkills(role) || canBookScreening(role));

  return {
    role,
    label,
    started: true,
    verified,
    seniority: role.verification.seniority,
    stopped,
    capacityWaitlisted: outcome?.stepKey === RoleStepKey.Review,
    outcomeLabel: outcome?.ctaLabel ?? null,
    scheduledAt: bookedAt ? formatScheduledSlot(bookedAt) : null,
    scheduledKind: scheduled?.kind ?? null,
    needsBooking,
    technicalDone: inProgress && isInterviewCompleted(role),
    waiting:
      inProgress &&
      !needsBooking &&
      !bookedAt &&
      isTestCompleted(role) &&
      isCallCompleted(role),
  };
};
